import WRAP from 'WRAP-JS';
import { nearValidTime } from "./MapUtil";
import { updateCrossSection } from "../../crosssection/FPECrossSection";

/**
 * 時制のコマ送り、再生を制御するクラス.
 * 親子構造(addTimeController)にすることで、親のTimeControllerでの操作を子のTimeControllerに伝搬する。
 */
class TimeController {
  /**システム内で一意のID（親コントローラーの場合はundefinedとする)*/
  id;
  /** 内包するWRAP.Geo.TimeController */
  controller;
  /** 内包するWRAP.Geo.Animation */
  animation;
  /** タイムコントローラが指している時間 */
  currentTime;
  /** 再生のインターバル */
  playInterval = 1;
  /** 繰り返し再生 */
  repeat = false;
  /** 自動更新 */
  autoRefresh = true;
  /** 再生中 */
  _animating = false;
  /** タイムコントローラからの再描画指示をうけるコールバック*/
  _invalidatedListener = [];
  /** 子のTimeControllerリスト */
  _cascade = [];
  /** 親のTimeController */
  _parent = null;
  /** 再生中のdisplayTimeの更新をブロック */
  _block = false;
  /** 再生させるtimeListの範囲。[0, timeList.length-1]で全範囲を再生 */
  _animationRange = null;
  /** 親コントローラから分離している状態 */
  _detachParent = false;

  constructor(id, option) {
    this.id = id;
    this.label = option?.label;
    this.controller = new WRAP.Geo.TimeController();
    this.animation = new WRAP.Geo.Animation();
    this.animation.setTimeController(this.controller);
    this.animation.setUpdating(this._updating.bind(this));
    this._timeList = option?.timeList || []; //getTimeListが未定義の場合のtimeList
    this._getTimeList = option?.getTimeList; //timelistを利用する側で定義する
    this._updatingListener = option?.updating ? [option.updating] : [];//コマ送り、アニメーションで時制が切り替わる直前のコールバック
    this._updatedListener = option?.updated ? [option.updated] : [] //コマ送り、アニメーションで時制が切り替わった際のコールバック
    this._changeAutoRefresh = option?.changeAutoRefresh || noop; //自動更新が切り替わった際のコールバック
    this._crossSection = !!option?.crossSection //断面図コンテンツの場合true
    this._crossSectionUpdate = this._crossSection ? updateCrossSection : noop
  }

  addLayer(layer) {
    //Layer#setVisibleをhookしてLayerをcontrollerに追加/削除する
    const orgMethod = layer.setVisible;
    layer.setVisible = function (visible) {
      orgMethod.apply(layer, [visible]);
      if (visible) {
        this.controller.addLayer(layer);
      } else {
        this.controller.removeLayer(layer);
      }
    }.bind(this);
  }

  /**
   * タイムコントローラが指す時間を切り替えて、気象コンテンツの表示時制を更新する.
   * @param displayTime
   * @param forced
   */
  setDisplayTime(displayTime, forced=false) {
    // 再生中はplay以外からのdisplayTimeの更新をブロック
    if (this._block && !forced) {
      return;
    }

    this._updating(displayTime);

    // 子コントローラーのsetDisplayTimeを実行
    const promises = this._cascadeApi('setDisplayTime', displayTime, forced) || [];

    this.setCurrentTime(displayTime);

    promises.push( new Promise(async(resolve) => {
      if (!this._crossSection) {
        //Mapコンテンツ
        this.controller.setDisplayTime(this.currentTime, () => {
          this._updated(this.currentTime);
          resolve();
        });
      } else {
        //断面図コンテンツ
        await this._crossSectionUpdate();
        this._updated(this.currentTime);
        resolve();
      }
    }));

    return promises.flat();
  }

  /**
   * タイムコントローラが指している時間を取得する.
   * @return {*}
   */
  getCurrentTime() {
    return this.currentTime;
  }

  /**
   * タイムコントローラが指す時間を切り替える.
   * @param currentTime
   * @param byAutoRefresh
   */
  setCurrentTime(currentTime, byAutoRefresh = null) {
    if (!currentTime || currentTime === '---') {
      return;
    }

    //親が自動更新Onで、子が自動更新Offの場合は、親の自動更新から設定された(byAutoRefresh=true) currentTimeは無視する。
    if (byAutoRefresh && this._parent?.autoRefresh && !this.autoRefresh) {
      return;
    }

    this.currentTime = this.controller._current_time = currentTime;

    this._cascadeApi('setCurrentTime', currentTime, byAutoRefresh);
  }

  getTimeList() {
    const timeList = (this._getTimeList && this._getTimeList());
    if (!timeList) {
      return this._timeList;
    }
    return timeList.sort();
  }

  setTimeList(timeList) {
    this._timeList = timeList;
  }

  hasNext() {
    const timelist = this.getTimeList();

    if(!timelist.length || !this.currentTime || this.animating) {
      return false;
    }
    return this.getCurrentPosition() < timelist.length -1;
  }

  hasPrev() {
    const timelist = this.getTimeList();

    if(!timelist.length || !this.currentTime || this.animating) {
      return false;
    }
    return this.getCurrentPosition() !== 0;
  }

  /**
   * タイムコントローラの利用可否状態.
   * 自身、子のいずれかが有効の場合、true
   * @return {boolean}
   */
  available() {
    const available = !!this.getTimeList().length && !!this.currentTime;
    if (this.hasChild()) {
      const a = [available].concat(this._cascadeApi('available'));
      return a.some(aa=>aa);
    } else {
      return available;
    }
  }

  /**
   * 親まで遡った再生状態取得.
   * 自身の再生状態がfalseでも親が再生中(且つ親と分離していない）ならtrue
   * 親の再生状態は遡って探索される.
   * 自身の再生状態のみ参照する場合は _animating を参照
   * @return {boolean|*}
   */
  get animating(){
    if (this._animating) {
      return true;
    }
    if (!this._parent || this._detachParent) {
      return false;
    }
    return this._parent.animating;
  }

  getCurrentPosition() {
    const timelist = this.getTimeList();
    if (!timelist.length || !this.currentTime) {
      return -1;
    }
    //タイムリストの中に指定された時間がない場合は一番近いものをpositionとする
    let currentPosition = timelist.indexOf(this.currentTime);
    if (currentPosition === -1) {
      const nearTime = nearValidTime(timelist, this.currentTime);
      currentPosition = timelist.indexOf(nearTime);
    }
    return currentPosition;
  }

  getOldestTime() {
    const timelist = this.getTimeList();
    if (!timelist.length) {
      return null;
    }
    return timelist[0];
  }

  getLatestTime() {
    const timelist = this.getTimeList();
    if (!timelist.length) {
      return null;
    }
    return timelist[timelist.length - 1];
  }

  first() {
    const timelist = this.getTimeList();

    if(!timelist.length || !this.currentTime || this.animating || !this.hasPrev()) {
      return;
    }
    this.setDisplayTime(timelist[0]);
  }

  prev() {
    const timelist = this.getTimeList();

    if(!timelist.length || !this.currentTime || this.animating || !this.hasPrev()) {
      return;
    }
    const idx = this.getCurrentPosition() - 1;
    this.setDisplayTime(timelist[idx]);
  }

  next() {
    const timelist = this.getTimeList();

    if(!timelist.length || !this.currentTime || this.animating || !this.hasNext()) {
      return;
    }
    const idx = this.getCurrentPosition() + 1;
    this.setDisplayTime(timelist[idx]);
  }

  last() {
    const timelist = this.getTimeList();

    if(!timelist.length || !this.currentTime || this.animating || !this.hasNext()) {
      return;
    }
    this.setDisplayTime(timelist[timelist.length - 1]);
  }

  play(completeCallback = noop, frameCallback = noop) {
    const timelist = this._animateTimeList(this.getTimeList());

    if(!timelist.length || !this.currentTime || (this._animating && !this.repeat)) {
      return;
    }

    if (this.id === undefined) {
      //子コントローラの再生を止める
      this._cascadeApiForce('pause');
    } else {
      //兄弟コントローラの再生を止める
      this._siblingApi('pause');
      this._siblingApi('invalidate');
    }

    //再生中のdisplayTimeの更新をブロック
    this._setBlock(true);

    this._setAnimationTimeRange(timelist);

    //currentTimeが再生範囲外の場合は一番近い時制から再生するように補正
    let curPos = timelist.indexOf(this.currentTime);
    if (curPos === -1) {
      const nearTime = nearValidTime(timelist, this.currentTime);
      curPos = timelist.indexOf(nearTime);
      this.setCurrentTime(nearTime);
    }
    if (curPos < timelist.length -1) {
      //setTimeRangeでcurrentの位置が初期化されるので、currentTimeの位置で補正
      //これを行わない場合、時制範囲の先頭からの再生となる
      this.animation.setTime({validtime: this.currentTime});
    }

    this._animating = true;
    this.animation.start(async (time)=> {
      if (this.hasChild()) {
        await Promise.all(this._cascadeApi('setDisplayTime', time.validtime, true).flat());
      }
      this.setCurrentTime(time.validtime);
      this._updated && this._updated(time.validtime);
      frameCallback(time);
      if (this._animating === false) {
        this.animation.stop();
        this._setBlock(false);
      }
      if (timelist.indexOf(this.currentTime) === timelist.length -1) {
        if (this.repeat && this._animating) {
          setTimeout(() => {
            if (this._animating !== false) {
              this.play(completeCallback, frameCallback);
            }
          }, this.playInterval * 1000);
        } else {
          this.pause();
          completeCallback();
        }
      }
    });
  }

  pause() {
    if (!this._animating) {
      return
    }

    this._animating = false;
    this._setBlock(false);
    this.animation._running = false;

    /*
     * reset silent mode
     * 再生中は次の時制のプレロードを行っている場合がある。そのタイミングで停止すると、次の時制を表示した状態で停止してしまう。
     * そのため、停止時には確定している時制で再描画し直す。
     */
    /* 最終の時制を表示している状態だと止めるタイミングでは_currentが-1になることがある。
     * その場合、_currentを最終時制にした上で停止する
     */
    if (this.animation._current === -1) {
      this.animation._current = this.animation._frame.length - 1;
    }

    this.setDisplayTime(this.animation._frame[this.animation._current].validtime, true);
    WRAP.Geo.removeAnimation(this.animation);

    this._cascadeApi('pause');
  }

  adjustCurrentTime() {
    //TimeListが変更されてcurrentTimeが範囲外になってしまった場合の調整
    if (this.getCurrentPosition() === -1) {
      if (this.currentTime < this.getOldestTime()) {
        this.first();
      } else if (this.getLatestTime() < this.currentTime) {
        this.last();
      }
    }
  }

  setPlayInterval(interval) {
    this.playInterval = interval;

    if (this._animating) {
      this.pause();
      this.play();
    }
  }

  setRepeat(repeat) {
    this.repeat = repeat;
  }

  changeAutoRefresh(autoMode) {
    this.autoRefresh = autoMode;
    this._changeAutoRefresh(autoMode);

    this._cascadeApi('changeAutoRefresh', autoMode);
  }

  addTimeController(timeController) {
    timeController._setParent(this);
    //親のblock状態を引き継ぐ
    timeController._block = this._block;
    timeController._detachParent = false;

    this._cascade.push(timeController);
  }

  removeTimeController(timeController) {
    const idx = this._cascade.findIndex(target => {
      return target.id === timeController.id;
    });
    if (idx === -1) return;
    const removed = this._cascade.splice(idx, 1)[0];
    //再生中の場合停止する
    if (removed._animating) {
      removed.pause();
    }
    removed._setParent(null);

    //再生中で子コントローラがいなくなった場合は停止する
    if (this._animating && this._cascade.length === 0) {
      this.pause();
    }

    removed.purgeChildTimeController();
  }

  purgeChildTimeController() {
    this._cascadeApi('purgeChildTimeController');
    this._cascade.forEach(timeController => {
      this.removeTimeController(timeController);
    });
  }

  getChildTimeController() {
    return this._cascade.filter(target => {
      return !target._detachParent;
    });
  }

  detachFromParent() {
    if (this._parent === null) {
      return;
    }
    this._detachParent = true;
  }

  joinParent() {
    if (this._parent === null) {
      return;
    }
    this._detachParent = false;
  }

  isLinkParent() {
    return !this._detachParent;
  }

  childSize() {
    return this._cascade.length;
  }

  hasChild() {
    return this.childSize() !== 0;
  }

  ids() {
    if (this.hasChild()) {
      return this._cascade.map(timeController => {
        return timeController.id;
      });
    } else {
      return [this.id];
    }
  }

  getLabel() {
    if (this.hasChild()) {
      if (this.label) {
        return [this.label].concat(this._cascadeApi('getLabel'));
      } else {
        return this._cascadeApi('getLabel');
      }

    } else {
      return this.label;
    }
  }

  setAnimationRange(range) {
    this._animationRange = range;
  }

  _setParent(timeController) {
    this._parent = timeController;
  }

  _setAnimationTimeRange(timeList) {
    const range = timeList.map(t => {
      return {
        validtime: t
      }
    });
    this.animation.setTimeRange(range, this.playInterval);
  }

  _animateTimeList(timeList) {
    if (!this._animationRange) {
      return timeList;
    }
    const min = Math.min(...this._animationRange);
    const max = Math.max(...this._animationRange);
    return timeList.filter((t, idx) => {
      return idx >= min && idx <= max
    });
  }

  /**
   * 子コントローラーのAPIを呼び出す.
   * @param api
   * @return {*[]}
   * @private
   */
  _cascadeApi(api) {
    if (!this.hasChild()) {
      return null;
    }

    const args = Array.prototype.slice.call(arguments, 1);
    const result = [];
    this._cascade.forEach(timeController => {
      if (timeController._detachParent) {
        return;
      }
      const ret = timeController[api].apply(timeController, args);
      if (ret !== undefined) {
        result.push(ret);
      }
    });
    return result;
  }

  /**
   * 子コントローラーのAPIを呼び出す(親からdetachしている状態でも呼び出す).
   * @param api
   * @return {*[]}
   * @private
   */
  _cascadeApiForce(api) {
    if (!this.hasChild()) {
      return null;
    }

    const args = Array.prototype.slice.call(arguments, 1);
    const result = [];
    this._cascade.forEach(timeController => {
      const ret = timeController[api].apply(timeController, args);
      if (ret !== undefined) {
        result.push(ret);
      }
    });
    return result;
  }

  /**
   * 親コントローラーのAPIを呼び出す.
   * @param api
   * @private
   */
  _parentApi(api) {
    if (!this._parent) {
      return;
    }
    const args = Array.prototype.slice.call(arguments, 1);
    const result = [];
    const ret = this._parent[api].apply(this._parent, args);
    if (ret !== undefined) {
      result.push(ret);
    }
    return result;
  }

  /**
   * 同じ親に紐づく兄弟コントローラのAPIを呼び出す.
   * @param api
   * @private
   */
  _siblingApi(api) {
    if (!this._parent) {
      return;
    }
    const args = Array.prototype.slice.call(arguments, 1);
    const result = [];
    this._parent._cascade.forEach(timeController => {
      if (timeController !== this) {
        const ret = timeController[api].apply(timeController, args);
        if (ret !== undefined) {
          result.push(ret);
        }
      }
    });
    return result;
  }

  /**
   * タイムコントローラにより気象コンテンツの時制が切り替えられる直前を検知するためのコールバックを設定する.
   * @param callback
   */
  addUpdatingListener(callback) {
    if (this._updatingListener.indexOf(callback) === -1) {
      this._updatingListener.push(callback);
    }
  }

  /**
   * タイムコントローラにより気象コンテンツの時制が切り替えられる直前を検知するためのコールバックを削除する.
   * @param callback
   */
  removeUpdatingListener(callback) {
    const idx = this._updatingListener.indexOf(callback);
    if (idx === -1) {
      return;
    }
    this._updatingListener.splice(idx, 1);
  }

  /**
   * タイムコントローラにより気象コンテンツの時制が切り替えられたことを検知するためのコールバックを設定する.
   * @param callback
   */
  addUpdatedListener(callback) {
    if (this._updatedListener.indexOf(callback) === -1) {
      this._updatedListener.push(callback);
    }
  }

  /**
   * タイムコントローラにより気象コンテンツの時制が切り替えられたことを検知するためのコールバックを削除する.
   * @param callback
   */
  removeUpdatedListener(callback) {
    const idx = this._updatedListener.indexOf(callback);
    if (idx === -1) {
      return;
    }
    this._updatedListener.splice(idx, 1);
  }

  /**
   * タイムコントローラ側からの再描画指示を受け取るためのコールバックを設定する
   * @param callback
   */
  addInvalidatedListener(callback) {
    if (this._invalidatedListener.indexOf(callback) === -1) {
      this._invalidatedListener.push(callback);
    }
  }

  /**
   * タイムコントローラ側からの再描画指示を受け取るためのコールバックを削除する
   * @param callback
   */
  removeInvalidatedListener(callback) {
    const idx = this._invalidatedListener.indexOf(callback);
    if (idx === -1) {
      return;
    }
    this._invalidatedListener.splice(idx, 1);
  }

  /**
   * タイムコントローラ側から再描画指示を通知する
   */
  invalidate() {
    this._invalidatedListener.forEach(callback => {
      callback();
    });
  }

  /**
   * タイムコントローラにより気象コンテンツの時制が切り替えられる直前を通知する.
   * @private
   */
  _updating(time) {
    this._updatingListener.forEach(callback => {
      callback(time);
    });
  }

  /**
   * タイムコントローラにより気象コンテンツの時制が切り替えられたことを通知する.
   * @private
   */
  _updated(time) {
    this._updatedListener.forEach(callback => {
      callback(time);
    });
  }

  /**
   * ブロックを設定する.
   * @param blocking
   * @private
   */
  _setBlock(blocking) {
    this._block = blocking;
    this._cascadeApi('_setBlock', blocking);
  }
}

const noop = () => {};

export default TimeController;